'use client';

import { useCallback, useRef, useState } from 'react';
import { loadTurnstile } from './TurnstileChallenge';

/** Shown when no token could be had: widget failed to load, errored, or the
 *  deploy has no site key. The server would drop the submission anyway. */
export const HUMAN_CHECK_FAILED =
  'We couldn’t confirm you’re a person. Please try again, or email support@rii.link.';

const TOKEN_WAIT_MS = 60_000;

/**
 * Cloudflare Turnstile on every submission, for forms that send email or page
 * someone (website#302). The server drops any submission without a valid
 * token, so the token is fetched BEFORE sending — no challenge round-trip for
 * a bot to learn from.
 *
 * The widget is managed + `interaction-only`: it stays invisible and passes
 * most people without a click, and only shows a checkbox when Cloudflare
 * wants one. It loads when the visitor first focuses the form (`arm`), so a
 * visitor who only reads the page never contacts Cloudflare.
 *
 * Usage: `<form onFocus={human.arm}>`, render `human.widget` inside the form,
 * then `const token = await human.getToken()` on submit and `human.reset()`
 * after each response (tokens are single-use).
 */
export function useHumanCheck() {
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
  const [armed, setArmed] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const widgetId = useRef<string | undefined>(undefined);
  const token = useRef<string | null>(null);
  const waiters = useRef<Array<(value: string | null) => void>>([]);

  const settle = useCallback((value: string | null) => {
    const pending = waiters.current;
    waiters.current = [];
    for (const resolve of pending) resolve(value);
  }, []);

  // A callback ref, not an effect: the host div unmounts when a form swaps to
  // its success panel and remounts on "send another", and each mount needs its
  // own widget.
  const setHost = useCallback(
    (el: HTMLDivElement | null) => {
      if (!el) {
        if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
        widgetId.current = undefined;
        token.current = null;
        return;
      }
      if (!siteKey) return;
      loadTurnstile()
        .then(() => {
          if (!el.isConnected || !window.turnstile || widgetId.current) return;
          widgetId.current = window.turnstile.render(el, {
            sitekey: siteKey,
            appearance: 'interaction-only',
            theme: 'light',
            'refresh-expired': 'auto',
            callback: (value: string) => {
              token.current = value;
              settle(value);
            },
            'expired-callback': () => {
              token.current = null;
            },
            'error-callback': () => {
              token.current = null;
              settle(null);
            },
          });
        })
        .catch(() => {
          setLoadFailed(true);
          settle(null);
        });
    },
    [siteKey, settle],
  );

  const arm = useCallback(() => setArmed(true), []);

  const getToken = useCallback((): Promise<string | null> => {
    if (!siteKey || loadFailed) return Promise.resolve(null);
    if (token.current) return Promise.resolve(token.current);
    setArmed(true);
    return new Promise((resolve) => {
      const done = (value: string | null) => {
        clearTimeout(timer);
        resolve(value);
      };
      const timer = setTimeout(() => {
        waiters.current = waiters.current.filter((w) => w !== done);
        resolve(token.current);
      }, TOKEN_WAIT_MS);
      waiters.current.push(done);
    });
  }, [siteKey, loadFailed]);

  const reset = useCallback(() => {
    token.current = null;
    if (widgetId.current && window.turnstile) window.turnstile.reset(widgetId.current);
  }, []);

  const widget = armed && siteKey ? <div ref={setHost} className="empty:hidden" /> : null;

  return { arm, getToken, reset, widget };
}
